import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export enum AuthType {
  google = "google",
  telegram = "telegram",
  email = "email",
}

export interface GoogleData {
  google_id: string;
  email: string;
  email_verified: boolean;
  picture: string;
  given_name: string;
  family_name: string;
  locale: string;
}

export interface UserData {
  isUserLoggedIn: boolean;
  authType: AuthType;
  userNickname: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  email?: string;
  telegramId?: number;
  userId: any;
  regDate: any;
  subscription?: number;
  googleData?: GoogleData | null;
  apiKey?: string;
  /**
   * Whether the server says this account holds the admin role.
   *
   * `undefined` means "not answered yet" and is NOT the same as `false` — the
   * admin screens must render a loading state for the first and the not-found
   * page for the second, or a non-admin briefly sees admin chrome.
   *
   * This is convenience only. Every admin action is re-authorised server-side
   * by `get_admin_user`; forging this flag yields an empty shell whose every
   * request is rejected.
   */
  isAdmin?: boolean;
  /**
   * Has the bootstrap finished deciding whether a stored session restores?
   *
   * `false` on a hard load until `AccountWrapper` has run its first restore
   * pass; set again by any bootstrap that ends a session (a 401 clear). Route
   * guards read THIS, never storage: storage can keep a session-looking
   * cookie that no bootstrap will act on again (a domain-scoped `ses_*` copy
   * `deleteCookie` cannot remove), and a guard reading it would wait forever.
   */
  sessionRestoreSettled: boolean;
}

const initialState: UserData = {
  isUserLoggedIn: false,
  authType: null,
  userNickname: "",
  avatarUrl: "",
  email: "",
  userId: "",
  regDate: "",
  subscription: null,
  googleData: null,
  apiKey: "",
  isAdmin: undefined,
  sessionRestoreSettled: false,
};

export const userDataSlice = createSlice({
  name: "userData",
  initialState,
  reducers: {
    setIsUserLoggedIn: (state, action) => {
      state.isUserLoggedIn = action.payload;
    },
    setAuthType: (state, action) => {
      state.authType = action.payload;
    },
    setUserNickname: (state, action) => {
      state.userNickname = action.payload;
    },
    setUserAvatar: (state, action) => {
      state.avatarUrl = action.payload;
    },
    setUserEmail: (state, action) => {
      state.email = action.payload;
    },
    setTelegramId: (state, action) => {
      state.telegramId = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setUserRegDate: (state, action) => {
      state.regDate = action.payload;
    },
    setSubscription: (state, action) => {
      state.subscription = action.payload;
    },
    setIsAdmin: (state, action) => {
      // Coerce here rather than at each call site: the API may omit the field,
      // and `undefined` must only ever mean "not answered yet".
      state.isAdmin = action.payload === true;
    },
    setGoogleData: (state, action) => {
      state.googleData = action.payload;
    },
    setApiKey: (state, action) => {
      state.apiKey = action.payload;
    },
    setSessionRestoreSettled: (state, action: PayloadAction<boolean>) => {
      state.sessionRestoreSettled = action.payload;
    },
  },
});

export const {
  setUserAvatar,
  setUserEmail,
  setAuthType,
  setUserNickname,
  setIsUserLoggedIn,
  setTelegramId,
  setUserId,
  setUserRegDate,
  setSubscription,
  setGoogleData,
  setApiKey,
  setIsAdmin,
  setSessionRestoreSettled,
} = userDataSlice.actions;

export default userDataSlice.reducer;
